/**
 * 客戶搜尋 Modal(REQ-20260418015 + REQ-20260419010 擴充)。
 *
 * 兩種搜尋模式:
 * - 客戶欄位 LIKE(原有):選某客戶欄位 + 關鍵字 → 列出客戶,點 → 跳到該客戶
 * - 附件檔名 LIKE(新增):選「附件檔名」+ 關鍵字 → 列出附件,點 →
 *   跳到對應客戶 + URL 帶 interviewId,操作台會自動 scroll + 高亮該訪談
 *
 * 兩張結果表都支援 ↑ / ↓ + Enter 選定(REQ-20260903010);沒有高亮時 Enter 維持送出搜尋。
 *
 * 還沒送出搜尋時列出該登入者的「最近搜尋」近 10 筆,點一筆等同再搜一次(REQ-20261027001)。
 */
import { useEffect, useRef, useState } from 'react';
import {
  CUSTOMER_SEARCHABLE_FIELDS,
  type CustomerSearchableField,
} from '@app/shared';
import { Modal } from '../../components/Modal';
import { useListKeyboardNav } from '../../components/useListKeyboardNav';
import { useAuthStore } from '../auth/store';
import { useCustomerFieldSearch } from './api';
import { useAttachmentSearch } from '../attachments/searchApi';
import { CUSTOMER_FIELD_LABELS } from './field-labels';
import {
  clearSearchHistory,
  pushSearchHistory,
  readSearchHistory,
  type SearchHistoryItem,
} from './searchHistory';

type SearchTarget = CustomerSearchableField | 'attachmentName';

const SEARCH_OPTIONS: ReadonlyArray<{ value: SearchTarget; label: string }> = [
  ...CUSTOMER_SEARCHABLE_FIELDS.map((f) => ({
    value: f as SearchTarget,
    label: CUSTOMER_FIELD_LABELS[f],
  })),
  { value: 'attachmentName', label: '附件檔名' },
];

const SEARCH_FIELDS = SEARCH_OPTIONS.map((opt) => opt.value);

type HistoryItem = SearchHistoryItem<SearchTarget>;

export interface SearchModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * 命中後回呼。
   * - 客戶模式:`field` 為 CustomerSearchableField,`opts.q` 帶搜尋關鍵字(REQ-20260421009 scoped paging)
   * - 附件模式:`field` 為 null,`opts.interviewId` 帶該附件所屬訪談 id
   */
  onPick: (
    customerId: number,
    field: CustomerSearchableField | null,
    opts?: { interviewId?: number; q?: string },
  ) => void;
  initialField?: SearchTarget;
}

export function SearchModal(props: SearchModalProps): JSX.Element {
  const { open, onClose, onPick, initialField = 'phone' } = props;
  const [field, setField] = useState<SearchTarget>(initialField);
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');

  const isAttachmentMode = field === 'attachmentName';
  const customerQuery = useCustomerFieldSearch({
    field: isAttachmentMode ? 'simpleName' : (field as CustomerSearchableField),
    q: isAttachmentMode ? '' : submitted,
  });
  const attachmentQuery = useAttachmentSearch(isAttachmentMode ? submitted : '');

  const userId = useAuthStore((s) => s.user?.id);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  // 點紀錄 / 清除的按鈕會跟著清單消失,焦點要拉回輸入框,↓ / Enter 才接得上(Modal 沒有 focus trap)
  const inputRef = useRef<HTMLInputElement>(null);
  // 換人登入就換一份;每次開窗重讀,另一個分頁剛搜過的也看得到
  useEffect(() => {
    setHistory(userId == null ? [] : readSearchHistory(userId, SEARCH_FIELDS));
  }, [userId, open]);

  const remember = (item: HistoryItem): void => {
    if (userId == null) return;
    setHistory(pushSearchHistory(userId, SEARCH_FIELDS, item));
  };

  const handleSubmit = (): void => {
    setSubmitted(q);
    // 只記「送出」的搜尋;點結果開啟(onPick)不另外記
    if (q.trim() !== '') remember({ field, q: q.trim() });
  };

  const handlePickHistory = (item: HistoryItem): void => {
    setField(item.field);
    setQ(item.q);
    setSubmitted(item.q);
    remember(item);
    inputRef.current?.focus();
  };

  const handleClearHistory = (): void => {
    if (userId != null) clearSearchHistory(userId);
    setHistory([]);
    inputRef.current?.focus();
  };

  // 鍵盤導覽的 count 取「當下這個模式實際列出來的筆數」;同一時間只會有一種模式
  const customerHits = customerQuery.data?.data ?? [];
  const attachmentHits = attachmentQuery.data?.data ?? [];
  const hitCount = isAttachmentMode ? attachmentHits.length : customerHits.length;

  const nav = useListKeyboardNav(hitCount, (i) => {
    if (isAttachmentMode) {
      const a = attachmentHits[i];
      if (a) onPick(a.customerId, null, { interviewId: a.interviewId });
      return;
    }
    const c = customerHits[i];
    if (c) onPick(c.id, field as CustomerSearchableField, { q: submitted });
  });

  // 換關鍵字(含還沒送出的打字)/ 換欄位 / 重新開窗都要清掉高亮 ——
  // 命中筆數剛好相同時 count 的 effect 抓不到;而打字期間清單仍是上一次的結果,
  // 留著高亮會讓 Enter 變成「開啟舊那筆」而不是重查(REQ-20260921001)
  const { setIndex } = nav;
  useEffect(() => {
    setIndex(-1);
  }, [submitted, q, field, open, setIndex]);

  return (
    <Modal open={open} onClose={onClose} title="進階搜尋" widthClass="max-w-3xl">
      <div className="grid gap-3">
        <div className="flex gap-2">
          <select
            value={field}
            onChange={(e) => {
              setField(e.target.value as SearchTarget);
              setSubmitted('');
            }}
            className="border rounded px-2 py-1.5"
            data-testid="search-field"
          >
            {SEARCH_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (nav.onKeyDown(e)) return;
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="輸入關鍵字後按 Enter"
            className="flex-1 border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
            data-testid="search-keyword"
            autoFocus
          />
          <button
            type="button"
            onClick={handleSubmit}
            className="bg-blue-600 text-white rounded px-3 py-1.5 hover:bg-blue-700"
          >
            搜尋
          </button>
        </div>

        <div className="min-h-[8rem]">
          {submitted === '' ? (
            history.length > 0 ? (
              <SearchHistoryList
                items={history}
                onPick={handlePickHistory}
                onClear={handleClearHistory}
              />
            ) : (
              <p className="text-slate-500 text-sm">請選欄位、輸入關鍵字後搜尋</p>
            )
          ) : isAttachmentMode ? (
            <AttachmentResults
              query={attachmentQuery}
              onPick={onPick}
              activeIndex={nav.index}
            />
          ) : (
            <CustomerResults
              query={customerQuery}
              field={field as CustomerSearchableField}
              q={submitted}
              onPick={onPick}
              activeIndex={nav.index}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

/** 沒有加 ↑ / ↓ 導覽:輸入框的 Enter 已經是「送出搜尋 / 選定結果」兩用,不再多一義 */
function SearchHistoryList(props: {
  items: ReadonlyArray<HistoryItem>;
  onPick: (item: HistoryItem) => void;
  onClear: () => void;
}): JSX.Element {
  const { items, onPick, onClear } = props;
  return (
    <div className="grid gap-1 text-sm">
      <div className="flex items-center justify-between">
        <span className="text-slate-500">最近搜尋</span>
        <button
          type="button"
          onClick={onClear}
          className="text-slate-500 hover:text-slate-800"
        >
          清除
        </button>
      </div>
      <ul data-testid="search-history">
        {items.map((h) => (
          <li key={`${h.field}:${h.q}`}>
            <button
              type="button"
              onClick={() => onPick(h)}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50"
              data-testid="search-history-item"
            >
              {SEARCH_OPTIONS.find((opt) => opt.value === h.field)?.label}：{h.q}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CustomerResults(props: {
  query: ReturnType<typeof useCustomerFieldSearch>;
  field: CustomerSearchableField;
  q: string;
  onPick: SearchModalProps['onPick'];
  /** 鍵盤高亮的列;-1 = 沒有高亮 */
  activeIndex: number;
}): JSX.Element {
  const { query, field, q, onPick, activeIndex } = props;
  if (query.isLoading) return <p className="text-slate-500 text-sm">搜尋中…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="text-red-600 text-sm">
        搜尋失敗：
        {query.error instanceof Error ? query.error.message : '未知錯誤'}
      </p>
    );
  }
  const hits = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;
  if (hits.length === 0) {
    return (
      <p className="text-slate-500 text-sm" data-testid="search-empty">
        查無資料
      </p>
    );
  }
  const overflow = total > hits.length;
  return (
    <div className="grid gap-2" data-testid="search-hits">
      {overflow ? (
        <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
          命中 {total} 筆，僅顯示前 {hits.length} 筆；請縮小關鍵字
        </div>
      ) : null}
      <table className="w-full text-sm border-collapse">
        <thead className="bg-slate-100 text-left">
          <tr>
            <th className="px-2 py-1.5">客戶編號</th>
            <th className="px-2 py-1.5">簡稱</th>
            <th className="px-2 py-1.5">{CUSTOMER_FIELD_LABELS[field]}</th>
            <th className="px-2 py-1.5"></th>
          </tr>
        </thead>
        <tbody>
          {hits.map((c, i) => (
            <tr
              key={c.id}
              // hover 變體會蓋掉高亮底色,所以兩者擇一輸出而不是並存
              className={`border-t cursor-pointer ${
                i === activeIndex ? 'bg-blue-100' : 'hover:bg-slate-50'
              }`}
              aria-selected={i === activeIndex ? true : undefined}
              data-testid={i === activeIndex ? 'kbd-active' : undefined}
              onClick={() => onPick(c.id, field, { q })}
            >
              <td className="px-2 py-1.5 font-mono text-slate-600">{c.cCode}</td>
              <td className="px-2 py-1.5">{c.simpleName}</td>
              <td className="px-2 py-1.5">{renderFieldValue(c, field)}</td>
              <td className="px-2 py-1.5">
                <span className="text-blue-600">→ 開啟</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AttachmentResults(props: {
  query: ReturnType<typeof useAttachmentSearch>;
  onPick: SearchModalProps['onPick'];
  /** 鍵盤高亮的列;-1 = 沒有高亮 */
  activeIndex: number;
}): JSX.Element {
  const { query, onPick, activeIndex } = props;
  if (query.isLoading) return <p className="text-slate-500 text-sm">搜尋中…</p>;
  if (query.isError) {
    return (
      <p role="alert" className="text-red-600 text-sm">
        搜尋失敗：
        {query.error instanceof Error ? query.error.message : '未知錯誤'}
      </p>
    );
  }
  const hits = query.data?.data ?? [];
  if (hits.length === 0) {
    return (
      <p className="text-slate-500 text-sm" data-testid="search-empty">
        查無附件
      </p>
    );
  }
  const hasMore = query.data?.meta.hasMore;
  const total = query.data?.meta.total ?? hits.length;
  return (
    <div className="grid gap-2" data-testid="search-hits-attachments">
      {hasMore ? (
        <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1">
          命中 {total} 筆，僅顯示前 {hits.length} 筆；請縮小關鍵字
        </div>
      ) : null}
      <table className="w-full text-sm border-collapse">
        <thead className="bg-slate-100 text-left">
          <tr>
            <th className="px-2 py-1.5">檔名</th>
            <th className="px-2 py-1.5">客戶簡稱</th>
            <th className="px-2 py-1.5">客戶編號</th>
            <th className="px-2 py-1.5">訪談類別</th>
            <th className="px-2 py-1.5">訪談日期</th>
            <th className="px-2 py-1.5"></th>
          </tr>
        </thead>
        <tbody>
          {hits.map((a, i) => (
            <tr
              key={a.id}
              className={`border-t cursor-pointer ${
                i === activeIndex ? 'bg-blue-100' : 'hover:bg-slate-50'
              }`}
              aria-selected={i === activeIndex ? true : undefined}
              data-testid={i === activeIndex ? 'kbd-active' : undefined}
              onClick={() =>
                onPick(a.customerId, null, { interviewId: a.interviewId })
              }
            >
              <td className="px-2 py-1.5 break-all">{a.originalName}</td>
              <td className="px-2 py-1.5">{a.customerSimpleName}</td>
              <td className="px-2 py-1.5 font-mono text-slate-600">{a.customerCCode}</td>
              <td className="px-2 py-1.5">{a.interviewTypeName ?? '—'}</td>
              <td className="px-2 py-1.5 text-slate-600">
                {/* 用本地時區顯示;直接 slice(0,10) 會取 UTC 日期,跨午夜差一天(Asia/Taipei) */}
                {a.interviewVisitedAt
                  ? new Date(a.interviewVisitedAt).toLocaleDateString('zh-TW')
                  : '—'}
              </td>
              <td className="px-2 py-1.5">
                <span className="text-blue-600">→ 開啟</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderFieldValue(
  c: { [key: string]: unknown },
  field: CustomerSearchableField,
): string {
  // phone 是虛擬欄位（REQ-20260421001）,串 customer_phones(PHONE 槽) 顯示
  if (field === 'phone') {
    const phones = (c.phones as Array<{ phoneType: string; number: string }> | undefined) ?? [];
    const nums = phones.filter((p) => p.phoneType === 'PHONE').map((p) => p.number);
    return nums.length > 0 ? nums.join(' / ') : '—';
  }
  const v = c[field];
  if (v == null) return '—';
  return String(v);
}
